/**
 * On-canvas inline text editing for the builder.
 *
 * `attachInlineEditable(el, field, opts)` turns one element into a
 * contenteditable region that reports edits to the admin via the bridge's
 * `emitInlineEdit(index, field, value)`. The owning block index is read from the
 * nearest `[data-block-index]` ancestor (the EditableBlock wrapper). On focus it
 * raises the inline-editing guard so streamed `blocks:set` updates don't yank the
 * caret; on debounced input / blur it commits. Returns a cleanup function.
 *
 * Two consumers:
 *  - `v-inline="'field'"` directive — for structured block components (Hero/CTA/
 *    RichText) that annotate a known field declaratively.
 *  - `useFreeformEditable` — wires every `[data-edit]` region inside a freeform
 *    block's `v-html` output (where a directive can't reach).
 *
 * No-ops entirely on the published site (no active bridge), so components can
 * carry the directive unconditionally.
 */
import { getActiveBridge } from "~/composables/useEditModeBridge";

export function attachInlineEditable(
  el,
  field,
  opts = {},
) {
  const bridge = getActiveBridge();
  if (!bridge || !bridge.editable) return () => {};

  const asHtml = opts.html ?? field === "html";
  el.setAttribute("contenteditable", "true");
  el.classList.add("hk-inline");
  if (asHtml) el.classList.add("hk-inline-rich"); // the formatting toolbar targets these
  el.setAttribute("data-inline", field);

  let timer = null;
  const readValue = () => (asHtml ? el.innerHTML : (el.innerText ?? "").trim());
  const commit = () => {
    const host = el.closest("[data-block-index]");
    if (!host) return;
    const index = Number(host.getAttribute("data-block-index"));
    if (Number.isNaN(index)) return;
    bridge.emitInlineEdit(index, field, readValue());
  };
  const onInput = () => { if (timer) clearTimeout(timer); timer = setTimeout(commit, 400); };
  const onFocus = () => bridge.setInlineEditing(true);
  const onBlur = () => { if (timer) clearTimeout(timer); commit(); bridge.setInlineEditing(false); };

  el.addEventListener("input", onInput);
  el.addEventListener("focus", onFocus);
  el.addEventListener("blur", onBlur);

  return () => {
    if (timer) clearTimeout(timer);
    el.removeEventListener("input", onInput);
    el.removeEventListener("focus", onFocus);
    el.removeEventListener("blur", onBlur);
    el.removeAttribute("contenteditable");
    el.classList.remove("hk-inline", "hk-inline-rich");
    el.removeAttribute("data-inline");
  };
}

const cleanups = new WeakMap();

export const vInline = {
  mounted(el, binding) {
    cleanups.set(el, attachInlineEditable(el, binding.value));
  },
  beforeUnmount(el) {
    cleanups.get(el)?.();
    cleanups.delete(el);
  },
};
